// src/client/screens/auth/SplashScreen.tsx
// Mounted on '/'. Auto-routes signed-in users; signed-out users see the
// editorial intro and tap "Get started" to enter /login.
//
// Stats ("avg approval", "members") are pulled live from the
// public.get_public_stats() RPC — anon-callable, aggregates only. The row
// is hidden until the response lands so we don't flash "—".
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@client/auth/useAuth';
import Button from '@client/components/Button';
import Wordmark from '@client/components/Wordmark';
import { supabase } from '@client/lib/supabase';

interface PublicStats {
  members: number;
  approvalSeconds: number | null;
}

// "969" → "16m 9s"; under a minute → "Ys"; over an hour → "Xh Ym"
function formatApproval(seconds: number): { primary: number; primaryUnit: 'h' | 'm' | 's'; secondary?: number; secondaryUnit?: 'm' | 's' } {
  if (seconds < 60) {
    return { primary: Math.max(1, Math.round(seconds)), primaryUnit: 's' };
  }
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0
      ? { primary: m, primaryUnit: 'm', secondary: s, secondaryUnit: 's' }
      : { primary: m, primaryUnit: 'm' };
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0
    ? { primary: h, primaryUnit: 'h', secondary: m, secondaryUnit: 'm' }
    : { primary: h, primaryUnit: 'h' };
}

// "1234" → "1,234". Adds a "+" suffix once the count is comfortably plural so
// "1+" never shows up.
function formatMembers(n: number): { value: string; plus: boolean } {
  return { value: n.toLocaleString('en-ZM'), plus: n >= 100 };
}

export default function SplashScreen() {
  const { status, profile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PublicStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase.rpc('get_public_stats').then((res) => {
      if (cancelled) return;
      const row = Array.isArray(res.data) ? res.data[0] : null;
      if (!row) return;
      setStats({
        members: Number(row.members_count ?? 0),
        approvalSeconds: row.avg_approval_seconds == null ? null : Number(row.avg_approval_seconds),
      });
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (status === 'loading') return;
    if (status === 'signed_out') return; // wait for the user to tap CTA
    if (profile && profile.kyc_status !== 'verified') {
      navigate(ROUTES.kyc, { replace: true });
      return;
    }
    navigate(ROUTES.home, { replace: true });
  }, [status, profile, navigate]);

  const goLogin = () => navigate(ROUTES.login);

  // Show the stats row only when at least one figure is meaningful.
  // Hides "1 member · 16m approval" while the project is brand-new and the
  // numbers would undersell the product, AND avoids the row flashing "—" on
  // first paint.
  const showStats = stats != null
    && (stats.members >= 25 || (stats.approvalSeconds != null && stats.approvalSeconds > 0));

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        minHeight: '100dvh',
        background: 'var(--c-bg)',
        // Top padding respects the device safe-area on real PWAs (notch /
        // dynamic island); falls back to 24px in browsers without support.
        padding: 'max(env(safe-area-inset-top), 24px) 24px calc(24px + env(safe-area-inset-bottom)) 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        maxWidth: 480,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div>
        <Wordmark size={20} contained />
      </div>

      {/* Editorial body — flex:1 keeps the buttons pinned to the bottom on
          tall viewports without leaving a giant void on short ones. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0, gap: 28 }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--ff-display)',
            fontSize: 'clamp(44px, 14vw, 60px)',
            lineHeight: 0.96,
            color: 'var(--c-text)',
            letterSpacing: -1.5,
            fontWeight: 400,
            margin: 0,
          }}>
            Credit, on
            <br />
            <em style={{ color: 'var(--c-accent)', fontStyle: 'italic' }}>your</em> terms.
          </h1>

          <p style={{
            marginTop: 18,
            fontSize: 15,
            lineHeight: 1.5,
            color: 'var(--c-text-sec)',
            maxWidth: 340,
          }}>
            Borrow K500–K20,000 in minutes. Repay via mobile money. Build credit that&apos;s actually yours.
          </p>
        </div>

        {showStats && stats && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
            {stats.approvalSeconds != null && (() => {
              const f = formatApproval(stats.approvalSeconds);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="eyebrow" style={{ fontSize: 10 }}>Avg. approval</span>
                  <span style={{
                    fontFamily: 'var(--ff-display)',
                    fontSize: 32,
                    color: 'var(--c-text)',
                    letterSpacing: -0.5,
                    lineHeight: 1,
                  }}>
                    {f.primary}<em style={{ fontStyle: 'italic' }}>{f.primaryUnit}</em>
                    {f.secondary != null && <> {f.secondary}<em style={{ fontStyle: 'italic' }}>{f.secondaryUnit}</em></>}
                  </span>
                </div>
              );
            })()}
            {stats.approvalSeconds != null && stats.members >= 25 && (
              <div style={{ width: 1, height: 32, background: 'var(--c-border)' }} />
            )}
            {stats.members >= 25 && (() => {
              const m = formatMembers(stats.members);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="eyebrow" style={{ fontSize: 10 }}>Members</span>
                  <span style={{
                    fontFamily: 'var(--ff-display)',
                    fontSize: 32,
                    color: 'var(--c-text)',
                    letterSpacing: -0.5,
                    lineHeight: 1,
                  }}>
                    {m.value}{m.plus && <em style={{ fontStyle: 'italic', fontSize: 22 }}>+</em>}
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Button block size="lg" onClick={goLogin} rightIcon={<ArrowRight size={18} />}>
          Get started
        </Button>
        <Button block variant="ghost" onClick={goLogin}>
          I have an account
        </Button>
      </div>
    </div>
  );
}
